import { useState, useCallback, useRef } from 'react';
import { createErrorNotification } from '../utils/createErrorNotification';

/**
 * Hook для управления удалением строки таблицы с диалогом подтверждения.
 *
 * @param {Object} params
 * @param {(item: object) => Promise<*>} params.onDelete   - async API-вызов удаления; должен вернуть response (не undefined) при успехе
 * @param {(item: object) => void}       params.onSuccess  - вызывается после успешного удаления
 */
const useDeleteRow = ({ onDelete, onSuccess }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Храним актуальные колбэки в refs, чтобы не включать их в deps-массив confirmDelete
  const callbacksRef = useRef({ onDelete, onSuccess });
  callbacksRef.current = { onDelete, onSuccess };

  const openDeleteDialog = useCallback(item => {
    setItemToDelete(item);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    if (isDeleting) return;
    setItemToDelete(null);
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      setIsDeleting(true);
      const result = await callbacksRef.current.onDelete(itemToDelete);
      if (result) {
        callbacksRef.current.onSuccess(itemToDelete);
        setItemToDelete(null);
      }
    } catch (error) {
      if (error?.response?.status === 422 && error?.response?.data?.detail) {
        createErrorNotification(error.response.data.detail);
      } else {
        createErrorNotification();
      }
      console.log(error.message);
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete]);

  return {
    isDeleting,
    itemToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  };
};

export default useDeleteRow;
